import React from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';

import { useTourStore, useTourStorageStore, TourPhaseId } from '@/stores/tour';

import { CustomTooltip } from './custom-tooltip';

interface ExtendedStep extends Step {
  phaseId?: TourPhaseId;
  profileId?: string;
}

const markStepProgress = (
  updateProgress: (id: TourPhaseId, profileId: string, stepId: string) => void,
  step: ExtendedStep
) => {
  if (step.phaseId && step.profileId) {
    updateProgress(step.phaseId, step.profileId, step.target as string);
  }
};

const markAllPhasesComplete = (
  updateProgress: (id: TourPhaseId, profileId: string, stepId: string) => void,
  steps: Step[]
) => {
  const phaseLastSteps = new Map<string, ExtendedStep>();
  for (const s of steps as ExtendedStep[]) {
    if (s.phaseId && s.profileId) {
      phaseLastSteps.set(`${s.phaseId}-${s.profileId}`, s);
    }
  }
  for (const lastStep of phaseLastSteps.values()) {
    markStepProgress(updateProgress, lastStep);
  }
};

export const GuidedTour: React.FC = () => {
  const { run, stepIndex, steps, setStepIndex, stopTour } = useTourStore();
  const updateProgress = useTourStorageStore(state => state.updateProgress);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, index, action, step } = data;
    const extStep = step as ExtendedStep;

    if (status === STATUS.FINISHED) {
      stopTour();
    } else if (status === STATUS.SKIPPED) {
      // Mark the last step of every phase as viewed so the tour doesn't restart
      markAllPhasesComplete(updateProgress, steps);
      stopTour();
    } else if (type === 'step:after' || type === 'error:target_not_found') {
      markStepProgress(updateProgress, extStep);
      setStepIndex(index + (action === 'prev' ? -1 : 1));
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton={false}
      run={run}
      scrollToFirstStep={false}
      showProgress
      showSkipButton
      scrollOffset={150}
      stepIndex={stepIndex}
      steps={steps}
      floaterProps={{
        styles: {
          floater: {
            marginTop: '20px', // Prevents the tooltip from hitting the absolute top edge of the mobile screen
          },
        },
      }}
      styles={{
        options: {
          zIndex: 2000,
        },
      }}
      tooltipComponent={CustomTooltip}
    />
  );
};
